import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

/**
 * The splash screen is the loader that shows while the app is loading.
 * The loading SVG is specified in the index.html so that it is shown immediately even before loading angular.
 * However, the splash screen needs to be hidden not only when angular is loaded, but when the app content is loaded and all images are rendered.
 *
 * That is why every app resolver has a call to this service .hide() function to manually hide the splash loader only after all images have been loaded
 * or, as a fallback, if enough time has passed (slow connection, some images are broken, etc).
 *
 *
 */
@Injectable({
  providedIn: 'root',
})
export class SplashScreenService {
  splashEl: HTMLElement;
  displayValue: string;

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.splashEl = this.document.querySelector(
      '.splash-screen'
    ) as HTMLElement;
    this.displayValue = this.splashEl.style.display;
  }

  /**
   * Wether the splash screen is still visible (loading) or not.
   * @returns
   */
  isSplashShown() {
    return (
      this.splashEl.style.display === this.displayValue &&
      !this.splashEl.classList.contains('fade-out')
    );
  }

  /**
   * Hides the splash screen.
   */
  hide() {
    if (this.isSplashShown()) {
      this.splashEl.classList.add('fade-out');
      setTimeout(() => {
        this.splashEl.style.display = 'none';
        this.splashEl.classList.remove('fade-out');
        const toHideElements = this.splashEl.querySelectorAll(
          '.hide-subsequent-loads'
        );
        toHideElements.forEach((el) => {
          el.remove();
        });
      }, 1000);
    }
  }

  show() {
    if (this.splashEl) {
      this.splashEl.style.display = this.displayValue;
    }
  }
}
